/** Shared helpers for turning Axios/DRF errors into user-facing text. */
import { t } from 'i18n';

const codes = {
  sign_in_required: 'session', session_expired: 'session', credentials_changed: 'session',
  invalid_input: 'invalidInput', invalid_password: 'invalidPassword', invalid_code: 'invalidCode',
  invalid_challenge: 'invalidChallenge', email_unverified: 'emailUnverified', email_required: 'emailRequired',
  no_membership: 'noMembership', already_enabled: 'alreadyEnabled', codes_unavailable: 'codesUnavailable',
  mfa_required: 'mfaRequired', not_found: 'notFound', permission_denied: 'forbidden',
  throttled: 'throttled', required: 'required', blank: 'required', invalid: 'invalidInput',
};
const knownMessages = {
  'This field is required.': 'required', 'This field may not be blank.': 'required',
  'Enter a valid email address.': 'invalidEmail', 'Enter a valid email.': 'invalidEmail',
  'Incorrect password.': 'invalidPassword', 'The current password is incorrect.': 'invalidPassword',
  'Invalid authenticator or recovery code.': 'invalidCode', 'Invalid authenticator code.': 'invalidCode',
  'No active account found with the given credentials': 'invalidCredentials',
  'Unable to log in with provided credentials.': 'invalidCredentials',
  'A dbt run is already in progress for this project.': 'dbtRunInProgress',
};

/** Only known product messages/codes are UI copy; upstream strings may contain private details. */
export function localizeApiMessage(message, code, fallback) {
  const key = (Object.hasOwn(codes, code) && codes[code]) || (Object.hasOwn(knownMessages, message) && knownMessages[message]);
  return key ? t(`common.${key}`) : (fallback || t('common.invalidInput'));
}

/**
 * Returns a user-facing error message from an Axios error, trying in order:
 * network/timeout, a known code or status-specific phrase, known DRF messages,
 * then a localized fallback. Arbitrary upstream error text is not interface copy.
 *
 * @param {unknown} err         - The caught error (assumed to be an Axios error)
 * @param {string}  fallback    - Message to return when nothing else matches
 * @returns {string}
 */
export function extractApiError(err, fallback) {
  if (!err?.response) {
    if (err?.code === 'ECONNABORTED' || err?.message?.toLowerCase().includes('timeout')) {
      return t('common.timeout');
    }
    return t('common.network');
  }

  const { status, data } = err.response;

  // Our phrasing for these statuses is always clearer than generic API text.
  if (data?.code && Object.hasOwn(codes, data.code)) return localizeApiMessage(null, data.code);
  if (status === 401) return t('common.session');
  if (status === 403) return t('common.forbidden');
  if (status === 404) return t('common.notFound');
  if (status === 429) return t('common.throttled');
  if (status >= 500) return t('common.server');

  if (typeof data === 'string' && data) return localizeApiMessage(data, null, fallback);
  if (data?.detail && typeof data.detail === 'string') return localizeApiMessage(data.detail, data.code, fallback);

  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length > 0) {
    return localizeApiMessage(data.non_field_errors[0], null, fallback);
  }

  // First field-level error (DRF sends arrays: { email: ['Enter a valid email.'] })
  if (typeof data === 'object' && data !== null) {
    for (const key of Object.keys(data)) {
      const val = data[key];
      const msg = Array.isArray(val) ? val[0] : typeof val === 'string' ? val : null;
      if (msg) return localizeApiMessage(msg, null, fallback);
    }
  }

  return fallback || t('common.error');
}

/**
 * Normalise a DRF field-error response so every value is a plain string, the
 * shape JsonSchemaForm expects: { email: ['Enter a valid email.'] } becomes
 * { email: 'Enter a valid email.' }. Non-field keys (detail, non_field_errors)
 * are excluded — they are not per-field validation messages.
 *
 * @param {unknown} data  - err.response.data
 * @returns {{ [field: string]: string }}
 */
export function normalizeFieldErrors(data) {
  if (typeof data !== 'object' || data === null) return {};
  const skip = new Set(['detail', 'non_field_errors', 'code']);
  const out = {};
  for (const [key, val] of Object.entries(data)) {
    if (skip.has(key)) continue;
    if (Array.isArray(val)) {
      if (val.length > 0) out[key] = localizeApiMessage(val[0]);
    } else if (typeof val === 'string') {
      out[key] = localizeApiMessage(val);
    }
  }
  return out;
}

/**
 * Returns true when the API error response contains per-field validation errors
 * (as opposed to a top-level detail message or a non-validation error).
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function hasFieldErrors(err) {
  const data = err?.response?.data;
  if (typeof data !== 'object' || data === null) return false;
  if (data.detail || data.non_field_errors) return false;
  return Object.keys(data).length > 0;
}
